import React, { useEffect,  useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, Plus, ShieldCheck } from "lucide-react";
import moment from "moment";
import toast from 'react-hot-toast';
import { onAuthStateChanged, signOut } from "firebase/auth";
import {  collection, doc, getDoc, onSnapshot, orderBy, query, where, getDocs } from "firebase/firestore";
import Modal from 'react-modal';
import { auth,db} from "../../firebase";
import Logo from '../../images/bcs-logo.png'
import { Calendar, Clock, X } from 'lucide-react';


// Flip to true to pause bookings and show the deferred notice again
const BOOKING_DEFERRED = false;

// Status styles
const statusStyles = {
  pending: "bg-[#fef9c3] text-[#F79000]",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
  Processing: "bg-purple-100 text-purple-700"
};

// Custom Modal Styles
const customModalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: '500px',
    width: '90%',
    borderRadius: '8px',
    padding: '24px'
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.75)'
  }
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [userBookings, setUserBookings] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [recentBooking, setRecentBooking] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState("");
  const [isOpen, setIsOpen] = useState(BOOKING_DEFERRED);

  const closeModal = () => setIsOpen(false);
  

  const newBookingPage = (usedCode,code) => {
  
    navigate("/new", {
      state: {
        code: code,
        usedCode: usedCode,
      }
    });
  };
  

  const fetchUserBookings = (setUserBookings) => {
    const user = auth.currentUser;

    if (!user) {
      toast.error("User not authenticated.");
      return () => { };
    }

    const userId = user.uid;
    const bookingsRef = query(
      collection(db, `members/${userId}/bookings`),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(
      bookingsRef,
      (snapshot) => {
        const bookings = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUserBookings(bookings);
        setLoading(false);
      },
      (error) => {
        setLoading(false);
        console.error("Error fetching bookings:", error);
        toast.error("Error fetching bookings.");
      }
    );

    return unsubscribe;
  };

  useEffect(() => {
    let unsubscribe = () => { };

    const authListener = onAuthStateChanged(auth, (user) => {
      if (user) {
        unsubscribe = fetchUserBookings(setUserBookings);
      } else {
        setUserBookings([]);
      }
    });

    return () => {
      unsubscribe();
      authListener();
    };
  }, []);

  // Effect to check recent bookings whenever userBookings changes
  useEffect(() => {
    if (userBookings.length > 0) {
      // Find the most recent booking
      const mostRecent = userBookings[0]; // Already sorted by createdAt desc
      
      if (mostRecent && mostRecent.createdAt) {
        const bookingDate = moment(mostRecent.createdAt.seconds * 1000);
        const now = moment();
        const daysDifference = now.diff(bookingDate, 'days');
        
        if (daysDifference <= 30) {
          setRecentBooking(mostRecent);
          const daysRemaining = 30 - daysDifference;
          setTimeRemaining(`${daysRemaining} days`);
        } else {
          setRecentBooking(null);
        }
      }
    }
  }, [userBookings]);

  

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        const userRef = doc(db, "members", currentUser.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          setUser(userSnap.data());
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate("/sign-in");
      toast.success("Signed out successfully");
    } catch (error) {
      toast.error("Error signing out!");
    }
  };


  const handleVerificationCodeChange = (e) => {
    // Only allow numbers and limit to 6 digits
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
    setVerificationCode(value);
  };

  const verifyCode = async () => {
    if (verificationCode.length !== 6) {
      toast.error("Please enter a valid 6-digit verification code");
      return;
    }
  
    setVerificationLoading(true);
    
    try {
      // Query the 'codes' collection for the entered code
      const codesRef = collection(db, "codes");
      const q = query(codesRef, where("code", "==", verificationCode));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        toast.error("Invalid verification code");
        setVerificationLoading(false);
        return;
      }
      
      // Check if the code has already been used
      const codeDoc = querySnapshot.docs[0];
      const codeData = codeDoc.data();
      
      if (codeData.used === true) {
        toast.error("This code has already been used");
        setVerificationLoading(false);
        return;
      }
      
      // Valid and unused code found
      setIsVerificationModalOpen(false);
      setVerificationLoading(false);
      newBookingPage(true, verificationCode);
      
    } catch (error) {
      console.error("Error verifying code:", error);
      toast.error("Error verifying code");
      setVerificationLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-white bg-opacity-50">
        <Loader2 className="animate-spin mr-2 text-gray-600" size={24} />
        <p className="text-lg text-gray-600">Please Wait...</p>
      </div>
    );
  }

  const navigateToBooking = (visitId) => {
    navigate(`/visit/${visitId}`);
  };

  const checkAndOpenNewBooking = () => {
    if (recentBooking) {
      // User has booking within 30 days, show verification modal
      setIsVerificationModalOpen(true);
    } else {
      // User doesn't have recent booking, proceed directly
      newBookingPage();
    }
  };
  


  return (
    <div className="flex flex-col items-center justify-start min-h-screen" 
      style={{
        backgroundImage: `url("https://i0.wp.com/www.angelsagainstabuse.org/wp-content/uploads/2018/11/blue-moving-flowing-abstract-waves-on-white-background-blurred-smooth-graphic-motion-design-video-animation-1920x1080_vhhueydte__F0000.png?fit=1920%2C1080&ssl=1")`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat'
      }}>

      {/* Navbar */}
      <nav className="w-full flex justify-between items-center px-6 py-6 bg-white shadow-md">
        <div>
          <img src={Logo} className="w-full" alt="BCS-LOGO" />
        </div>
        <div className="flex items-center space-x-4">
          <div
            onClick={handleSignOut}
            className="p-2 bg-gray-200 rounded-full cursor-pointer">
            <LogOut size={20} />
          </div>
        </div>
      </nav>

      {/* User Profile */}
      <div className="flex flex-col items-center mb-6 mt-8">
        <img
          src={user?.profile_image || "https://img.freepik.com/free-psd/contact-icon-illustration-isolated_23-2151903337.jpg"}
          alt="Avatar"
          className="h-36 w-36 rounded-full object-cover bg-gray-400"
        />
        <h1 className="mt-4 text-xl font-semibold">{user?.first_name} {user?.last_name}</h1>
        <p className="text-gray-500">{user?.email}</p>
      </div>

      {/* Bookings Section */}
      <div className="flex flex-col w-full max-w-md">
        <div className="flex items-center justify-between mb-8 px-5">
          <div className="flex gap-4">
            <button className="text-lg font-medium px-4 py-2 border-b-2 border-black">
              My Bookings
            </button>
          </div>
          <button
            onClick={
              BOOKING_DEFERRED
                ? () => setIsOpen(true)
                : checkAndOpenNewBooking
            }
            className="ml-auto bg-[#2563EB] flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring focus:ring-blue-300"
          >
            <Plus size={16} color="#fff" />
            New Booking
          </button>
        </div>

      {/* Modal Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn"
          onClick={closeModal}
        >
          {/* Modal Content */}
          <div
            className="relative bg-white rounded-3xl shadow-2xl max-w-5xl w-full mx-4 transform animate-slideIn overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Decorative gradient border */}
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-indigo-500 to-purple-500 rounded-3xl">
              <div className="bg-white rounded-3xl h-full w-full">
              </div>
            </div>

            {/* Content Container */}
            <div className="relative z-10 bg-white rounded-3xl">
              {/* Header */}
              <div className="relative bg-gradient-to-br from-purple-600 via-indigo-600 to-purple-700 text-white p-8 rounded-t-3xl">
                <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-10"></div>
                
                <div className="relative z-10 flex flex-col items-center text-center">
                <button
                  onClick={closeModal}
                  className="absolute z-50 top-6 cursor-pointer right-6 text-white hover:text-purple-200 transition-colors duration-200 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full p-2"
                >
                  <X className="w-5 h-5" />
                </button>
                  <div className="bg-white bg-opacity-15 backdrop-blur-sm p-4 rounded-2xl mb-4 mt-10">
                    {/* <Church className="w-8 h-8" /> */}
                    <img src={Logo} className="w-20" alt="BCS-LOGO" />
                  </div>
                  <h2 className="text-2xl font-bold mb-2">Holy Vestry</h2>
                  <p className="text-purple-100 text-sm">Booking Portal</p>
                </div>
              </div>

              {/* Body */}
              <div className="p-0 text-center">
                <div className="mb-4">
                  <div className="relative mx-auto w-24 h-24 my-4">
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-full animate-pulse"></div>
                    <div className="absolute inset-2 bg-white rounded-full flex items-center justify-center">
                      <Clock className="w-10 h-10 text-purple-600" />
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    <h3 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                      Booking Deferred
                    </h3>
                    
                    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl p-6 border border-purple-100">
                      <p className="text-lg font-bold text-gray-800 mb-3">
                       SORRY HOLY VESTRY BOOKING IS DEFERRED TO A LATER DATE.
                      </p>
                      <p className="text-base text-gray-700 mb-4">
                        PLEASE CHECK BACK IN THREE WEEKS.
                      </p>
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-8 h-0.5 bg-gradient-to-r from-purple-400 to-indigo-400"></div>
                        <p className="text-lg font-semibold text-purple-700 italic">
                          PEACE
                        </p>
                        <div className="w-8 h-0.5 bg-gradient-to-r from-indigo-400 to-purple-400"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-10 pb-10">
                <button
                  onClick={closeModal}
                  className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold py-4 px-6 rounded-2xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
                >
                  I Understand
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Styles */}
      <style jsx>{`
        @keyframes fadeIn {
          from { 
            opacity: 0; 
          }
          to { 
            opacity: 1; 
          }
        }
        
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-30px) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        
        .animate-slideIn {
          animation: slideIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      `}</style>

        {/* Bookings List */}
        <div className="w-full space-y-6">
          {userBookings?.length === 0 ? (
            <div className="flex justify-center items-center mt-6">
              <p>You have no bookings!</p>
            </div>
          ) : (
            userBookings?.map((booking) => {
              // Handle both array and string formats for backward compatibility
              let memberNamesDisplay = "";
              if (booking?.groupMemberNames) {
                if (Array.isArray(booking.groupMemberNames)) {
                  const validNames = booking.groupMemberNames.filter(name => name && name.trim());
                  memberNamesDisplay = validNames.slice(0, 2).join(', ');
                  if (validNames.length > 2) {
                    memberNamesDisplay += '...';
                  }
                } else if (typeof booking.groupMemberNames === 'string') {
                  const names = booking.groupMemberNames.split('\n').filter(n => n.trim());
                  memberNamesDisplay = names.slice(0, 2).join(', ');
                  if (names.length > 2) {
                    memberNamesDisplay += '...';
                  }
                }
              }

              return (
                <div
                  key={booking.id}
                  onClick={() => navigateToBooking(booking.id)}
                  className="p-4 border rounded-lg shadow-sm flex items-center gap-4 cursor-pointer border-[#eee]"
                >
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[#eee]">
                    <Calendar className="text-gray-600" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold">
                      {booking?.purpose || booking?.customPurpose}
                      <span className="text-gray-500 text-xs"> ({booking?.visitType}) </span>
                    </h3>
                    <p className="text-sm text-gray-500">
                      {moment(booking?.createdAt?.seconds * 1000).format("MMMM D, YYYY [at] h:mm A") || "Invalid Date."}
                    </p>
                    {memberNamesDisplay && (
                      <p className="text-xs text-gray-600 mt-1">
                        <strong>Group Members:</strong> {memberNamesDisplay}
                      </p>
                    )}
                  </div>
                  <span className={`text-sm font-medium px-3 py-1 rounded-full ${statusStyles[booking?.status]}`}>
                    {booking.status}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* 30-Day Verification Modal */}
      <Modal
        isOpen={isVerificationModalOpen}
        onRequestClose={() => setIsVerificationModalOpen(false)}
        style={customModalStyles}
        contentLabel="Verification Modal"
      >
        <div className="flex flex-col items-center">
          <div className="bg-blue-100 p-4 rounded-full mb-4">
            <ShieldCheck size={40} className="text-blue-600" />
          </div>
          
          <h2 className="text-xl font-bold text-center mb-2">Verification Required</h2>
          
          <div className="text-center mb-6">
            <p className="text-gray-700 mb-2">
              You already have a booking within the last 30 days.
            </p>
            <p className="text-gray-500 text-sm">
              Time remaining until your next booking: <span className="font-semibold">{timeRemaining}</span>
            </p>
          </div>
          
          <div className="w-full mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Enter verification code to proceed
            </label>
            
            <div className="relative">
              <input
                type="text"
                value={verificationCode}
                onChange={handleVerificationCodeChange}
                placeholder="6-digit code"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-lg font-medium tracking-widest"
                maxLength={6}
              />
            </div>
            
            <p className="text-xs text-gray-500 mt-2">
              Enter the 6-digit code provided. You can only use your code once.
            </p>
          </div>
          
          <div className="flex w-full gap-3">
            <button
              onClick={() => setIsVerificationModalOpen(false)}
              className="flex-1 py-2 bg-[#eee] px-4 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            
            <button
              onClick={verifyCode}
              disabled={verificationCode.length !== 6 || verificationLoading}
              className="flex-1 py-2 px-4 bg-[#2563EB] text-white rounded-lg flex justify-center items-center gap-2 hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {verificationLoading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Verifying
                </>
              ) : (
                "Verify"
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Dashboard;